import { ArrowRightIcon, CheckIcon, XIcon } from '@phosphor-icons/react'
import { mappedColors } from '@pixee/design-system'
import * as Dialog from '@radix-ui/react-dialog'
import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'
import { match } from 'ts-pattern'
import { DefaultButton } from '../../components/buttons/default-button.tsx'
import { useRepositoryActionMenuStore } from '../../components/menus/repository-action-menu.store.tsx'
import { RenderedMarkdown } from '../../components/rendered-markdown.tsx'
import { Spinner } from '../../components/spinner.tsx'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { Routes } from '../../routes.ts'
import {
  invalidateGetRepositoryExploitChains,
  useExploitChainsStream,
  useGetRepository,
  useGetRepositoryBranches,
  useGetRepositoryExploitChains,
  useGetRepositoryThreatModel,
  useGetScans,
  usePostRepositoryExploitChains,
} from '../../utils/api-client/api-hooks.ts'
import {
  type ChainSummary,
  type ExploitChainsPhase,
  ExploitChainsPhaseSchema,
  isInFlightGenerationStatus,
  type LinkedFinding,
} from '../../utils/api-client/api-schemas.ts'
import { formatDate, formatRelativeTime } from '../../utils/date-utils.ts'
import { scanIdFromFindingBackreference } from '../../utils/finding-utils.ts'
import { joinMetaSegments, pluralize } from '../../utils/text-utils.ts'
import { CorrelationCanvas } from './correlation-canvas.tsx'
import { ExploitChainsRunSummary } from './exploit-chains-run-summary.tsx'
import { type RunState, runStateFromStream, runStateFromSummary } from './exploit-chains-run.ts'
import * as styles from './exploit-chains-tab.css.ts'
import { HypothesisLog } from './hypothesis-log.tsx'
import { PhaseStepper } from './phase-stepper.tsx'
import { useRepositoryDetailsPageParams } from './repository-details-page.hooks.tsx'

// `unknown` is a fallback the backend reports when it can't determine the live phase; it is not a
// real generation step, so it is excluded from the stepper's ordered phases.
type SupportedExploitChainsPhase = Exclude<ExploitChainsPhase, 'unknown'>

// The type predicate narrows the element type so `SUPPORTED_EXPLOIT_CHAINS_PHASES` is
// `SupportedExploitChainsPhase[]`, which forces callers to reconcile the wider `unknown`-bearing
// phase (via `toSupportedPhase`) before it can reach the stepper.
const SUPPORTED_EXPLOIT_CHAINS_PHASES = ExploitChainsPhaseSchema.options.filter(
  (phase): phase is SupportedExploitChainsPhase => phase !== 'unknown'
)

// The live phase can be `unknown` (indeterminate); the stepper only knows the real phases, so map
// `unknown` to the first phase to render an in-flight state instead of mis-indexing on it.
const toSupportedPhase = (phase: ExploitChainsPhase): SupportedExploitChainsPhase =>
  phase === 'unknown' ? 'queued' : phase

const PHASE_LABELS = {
  queued: 'Queued',
  fetching_code: 'Fetching code',
  loading_inputs: 'Loading inputs',
  correlating: 'Correlating findings',
  rendering: 'Rendering',
} satisfies Record<SupportedExploitChainsPhase, string>

type SeverityLevel = 'critical' | 'high' | 'medium' | 'low' | 'unknown'

const toSeverityLevel = (severity: string): SeverityLevel =>
  match(severity.trim().toLowerCase())
    .with('critical', () => 'critical' as const)
    .with('high', () => 'high' as const)
    .with('medium', 'moderate', () => 'medium' as const)
    .with('low', () => 'low' as const)
    .otherwise(() => 'unknown' as const)

export type ExploitChainsTabProps = {
  onSwitchToThreatModel: () => unknown
}

export const ExploitChainsTab: React.FC<ExploitChainsTabProps> = ({ onSwitchToThreatModel }) => {
  const { repositoryId } = useRepositoryDetailsPageParams()
  const queryClient = useQueryClient()
  const setCurrentOverlay = useRepositoryActionMenuStore(state => state.setCurrentOverlay)

  const { data: repository, isLoading: isRepositoryLoading } = useGetRepository({ repositoryId })
  const { data: threatModel, isLoading: isThreatModelLoading } = useGetRepositoryThreatModel({ repositoryId })
  const defaultBranch = repository?.default_branch
  const branchesHref = repository?._links.branches?.href
  // The default branch, from any tool: listed when it has at least one scan, absent when it has none.
  const { data: defaultBranchListing, isLoading: isDefaultBranchListingLoading } = useGetRepositoryBranches({
    branchesHref,
    names: defaultBranch ? [defaultBranch] : [],
    page: { number: 0, size: 1 },
    enabled: !!defaultBranch,
  })
  // A platform without the branches endpoint omits the link; its scans list answers the same question.
  const { data: defaultBranchScans, isLoading: isDefaultBranchScansLoading } = useGetScans({
    page: { number: 0, size: 1 },
    repositoryIds: [repositoryId],
    branch: defaultBranch,
    enabled: !!repository && !branchesHref && !!defaultBranch,
  })
  const isDefaultBranchLoading = isDefaultBranchListingLoading || isDefaultBranchScansLoading
  const { data: exploitChains, isLoading: isExploitChainsLoading } = useGetRepositoryExploitChains({ repositoryId })

  // Any viewer's GET can report an in-flight status (single-flight generation may have been
  // triggered elsewhere); the stream and polling both key off the server state. `unknown` counts as
  // in-flight so the stream/poll keep running until the status resolves — see the render `match`.
  const isInFlight = !!exploitChains && isInFlightGenerationStatus(exploitChains.status)
  const stream = useExploitChainsStream(isInFlight ? repositoryId : undefined)
  const lastStreamEvent = stream.events.at(-1)
  const firstStreamEvent = stream.events.at(0)
  // The stream is the run's story while it is in flight; folded into one state it drives the
  // canvas and the hypothesis log, and the stepper keeps reading the phase off the same events.
  const liveRun = useMemo(() => runStateFromStream(stream.events), [stream.events])

  useEffect(() => {
    if (lastStreamEvent && lastStreamEvent.status !== 'processing') {
      invalidateGetRepositoryExploitChains(queryClient, { repositoryId })
    }
  }, [lastStreamEvent, queryClient, repositoryId])

  const showGenerateError = useShowMutationError<typeof usePostRepositoryExploitChains>({
    prefix: 'Failed to start exploit chains generation',
  })
  const generateMutation = usePostRepositoryExploitChains({
    onSuccess: () => invalidateGetRepositoryExploitChains(queryClient, { repositoryId }),
    onError: showGenerateError,
  })
  const isStarting = generateMutation.isPending
  // Depend on the stable `mutate`, not the mutation object (which changes on every state flip).
  const onGenerate = useCallback(
    () => generateMutation.mutate({ repositoryId }),
    [generateMutation.mutate, repositoryId]
  )

  const [selectedChainId, setSelectedChainId] = useState<string>()

  // The threat model self-loads inside the analysis-service agent; the platform only needs a
  // completed threat model to exist, which the content HAL link signals.
  const threatModelReady = !!threatModel?._links.content
  const defaultBranchScanned = branchesHref
    ? (defaultBranchListing?._embedded.items.length ?? 0) > 0
    : (defaultBranchScans?.total ?? 0) > 0
  const prerequisitesMet = threatModelReady && defaultBranchScanned

  if (isExploitChainsLoading || isThreatModelLoading || isRepositoryLoading || isDefaultBranchLoading) {
    return (
      <div className={styles.loadingState}>
        <Spinner label="Loading exploit chains" />
      </div>
    )
  }

  // Nothing generated yet: gate on prerequisites before offering generation.
  if (!exploitChains) {
    return prerequisitesMet ? (
      <ExploitChainsEmptyState onGenerate={onGenerate} isStarting={isStarting} />
    ) : (
      <ExploitChainsPrerequisiteChecklist
        threatModelReady={threatModelReady}
        defaultBranchScanned={defaultBranchScanned}
        onGenerateThreatModel={onSwitchToThreatModel}
        onRunScan={() => setCurrentOverlay('add-findings')}
      />
    )
  }

  const currentPhase = toSupportedPhase(lastStreamEvent?.phase ?? exploitChains.phase)
  const startedAt = firstStreamEvent?.timestamp
  const chains = exploitChains.chains ?? []
  const hasChains = chains.length > 0
  const selectedChain = chains.find(chain => chain.chainId === selectedChainId)
  // A completed run explains itself from its stored summary; runs completed before the summary
  // existed have none and fall back to the plain empty state.
  const completedRun = exploitChains.correlation
    ? runStateFromSummary({
        summary: exploitChains.correlation,
        phase: exploitChains.phase,
        status: exploitChains.status,
      })
    : undefined

  return (
    <div className={styles.container}>
      {match(exploitChains.status)
        // `unknown` is an indeterminate status; treat it as in-flight so polling can resolve it.
        .with('processing', 'unknown', () =>
          hasChains ? (
            <>
              <ExploitChainsRegeneratingBanner
                phase={currentPhase}
                requestedBy={exploitChains.requestedBy}
                startedAt={startedAt}
              />
              <ExploitChainCardList chains={chains} onSelect={setSelectedChainId} isDimmed={true} />
            </>
          ) : (
            <ExploitChainsGeneratingState
              phase={currentPhase}
              run={liveRun}
              requestedBy={exploitChains.requestedBy}
              startedAt={startedAt}
            />
          )
        )
        .with('failed', () => (
          <>
            <ExploitChainsFailedBanner
              failureReason={exploitChains.failureReason}
              onRetry={onGenerate}
              isRetrying={isStarting}
              canRetry={prerequisitesMet}
            />
            {hasChains && <ExploitChainCardList chains={chains} onSelect={setSelectedChainId} />}
          </>
        ))
        .with('completed', () => (
          <>
            <ExploitChainsMetaRow
              generatedAt={exploitChains.generatedAt}
              branch={defaultBranch}
              commitSha={exploitChains.commitSha}
              requestedBy={exploitChains.requestedBy}
              chainCount={chains.length}
              onRegenerate={onGenerate}
              isStarting={isStarting}
              canRegenerate={prerequisitesMet}
            />
            {completedRun && <ExploitChainsRunSummary run={completedRun} />}
            {hasChains ? (
              <ExploitChainCardList chains={chains} onSelect={setSelectedChainId} />
            ) : (
              !completedRun && <ExploitChainsNoChainsState />
            )}
          </>
        ))
        .exhaustive()}
      {selectedChain && (
        <ExploitChainDetailDrawer
          chain={selectedChain}
          branch={defaultBranch}
          onClose={() => setSelectedChainId(undefined)}
        />
      )}
    </div>
  )
}

// #region Prerequisite checklist

type ExploitChainsPrerequisiteChecklistProps = {
  threatModelReady: boolean
  defaultBranchScanned: boolean
  onGenerateThreatModel: () => unknown
  onRunScan: () => unknown
}

const ExploitChainsPrerequisiteChecklist: React.FC<ExploitChainsPrerequisiteChecklistProps> = ({
  threatModelReady,
  defaultBranchScanned,
  onGenerateThreatModel,
  onRunScan,
}) => (
  <div className={styles.checklist}>
    <div className={styles.checklistIntro}>
      <p className={styles.checklistTitle}>Exploit chains need a little setup</p>
      <p className={styles.checklistDescription}>
        Exploit chains correlate your triaged findings into multi-step attack paths. Complete both prerequisites to
        enable generation.
      </p>
    </div>
    <ul className={styles.checklistItems}>
      {/* Scan first: a default-branch scan usually exists before a threat model is generated. */}
      <ExploitChainsChecklistItem
        isMet={defaultBranchScanned}
        label="Scan on the default branch"
        metText="The default branch has at least one scan."
        actionLabel="Add findings"
        onAction={onRunScan}
      />
      <ExploitChainsChecklistItem
        isMet={threatModelReady}
        label="Threat model generated"
        metText="A completed threat model is available."
        actionLabel="Generate a threat model"
        onAction={onGenerateThreatModel}
      />
    </ul>
  </div>
)

type ExploitChainsChecklistItemProps = {
  isMet: boolean
  label: string
  metText: string
  actionLabel: string
  onAction: () => unknown
}

const ExploitChainsChecklistItem: React.FC<ExploitChainsChecklistItemProps> = ({
  isMet,
  label,
  metText,
  actionLabel,
  onAction,
}) => {
  const state = isMet ? ('met' as const) : ('unmet' as const)

  return (
    <li className={styles.checklistItem} aria-label={`${label}: ${isMet ? 'met' : 'not met'}`}>
      <span className={styles.checklistIndicator({ state })} aria-hidden="true">
        {isMet && <CheckIcon size={12} weight="bold" />}
      </span>
      <div className={styles.checklistItemBody}>
        <p className={styles.checklistItemLabel({ state })}>{label}</p>
        {isMet ? (
          <p className={styles.checklistItemMet}>{metText}</p>
        ) : (
          <button className={styles.checklistAction} onClick={onAction}>
            {actionLabel}
          </button>
        )}
      </div>
    </li>
  )
}

// #endregion

// #region Empty / no-chains states

type ExploitChainsEmptyStateProps = {
  onGenerate: () => unknown
  isStarting: boolean
}

const ExploitChainsEmptyState: React.FC<ExploitChainsEmptyStateProps> = ({ onGenerate, isStarting }) => (
  <div className={styles.emptyState}>
    <p className={styles.emptyStateTitle}>No exploit chains yet</p>
    <p className={styles.emptyStateDescription}>
      Generate exploit chains to correlate this repository's triaged findings into multi-step attack paths. Generation
      usually takes a few minutes.
    </p>
    <DefaultButton buttonType="primary" state={isStarting ? 'loading' : 'default'} onClick={onGenerate}>
      Generate exploit chains
    </DefaultButton>
  </div>
)

const ExploitChainsNoChainsState: React.FC = () => (
  <div className={styles.emptyState} data-testid="exploit-chains-no-chains">
    <p className={styles.emptyStateTitle}>No exploit chains identified</p>
    <p className={styles.emptyStateDescription}>
      No multi-step attack paths were found across this repository's triaged findings. This is a healthy result.
    </p>
  </div>
)

// #endregion

// #region Generation progress

type ExploitChainsGeneratingStateProps = {
  phase: SupportedExploitChainsPhase
  run: RunState
  requestedBy?: string
  startedAt?: string
}

/**
 * The in-flight view: the stepper for where the run is, the correlation canvas for what it is
 * doing, and the hypothesis log for what it has concluded so far. Hovering a hypothesis in the
 * log lights its chord on the canvas, and hovering a finding on the canvas lifts its hypotheses.
 */
const ExploitChainsGeneratingState: React.FC<ExploitChainsGeneratingStateProps> = ({
  phase,
  run,
  requestedBy,
  startedAt,
}) => {
  const [highlightedId, setHighlightedId] = useState<string>()
  const hasStory = run.tools !== undefined || run.hypotheses.length > 0

  return (
    <div className={styles.generatingState}>
      <PhaseStepper
        phases={SUPPORTED_EXPLOIT_CHAINS_PHASES}
        labels={PHASE_LABELS}
        currentPhase={phase}
        currentColor={mappedColors.primary.pulse}
        currentTextColor={mappedColors.severity.text}
      />
      <div className={styles.generatingMeta}>
        <p className={styles.generatingTitle}>
          <Spinner size="16" label="Generating exploit chains" />
          Generating exploit chains
        </p>
        <p className={styles.metaText}>
          {joinMetaSegments([
            requestedBy && `Requested by ${requestedBy}`,
            startedAt && `Started ${formatRelativeTime(startedAt)}`,
          ])}
        </p>
      </div>
      <div className={styles.liveLayout}>
        <CorrelationCanvas run={run} highlightedId={highlightedId} onHighlightFinding={setHighlightedId} />
        <div className={styles.liveLog}>
          {hasStory ? (
            <HypothesisLog
              run={run}
              isLive={true}
              highlightedId={highlightedId}
              onHighlightHypothesis={setHighlightedId}
            />
          ) : (
            <p className={styles.hintText}>
              The log fills in as findings are loaded, hypotheses are proposed and each one is verified against the
              source.
            </p>
          )}
        </div>
      </div>
      <p className={styles.hintText}>
        Usually takes a few minutes. You can leave this page — generation continues in the background.
      </p>
    </div>
  )
}

type ExploitChainsRegeneratingBannerProps = {
  phase: SupportedExploitChainsPhase
  requestedBy?: string
  startedAt?: string
}

const ExploitChainsRegeneratingBanner: React.FC<ExploitChainsRegeneratingBannerProps> = ({
  phase,
  requestedBy,
  startedAt,
}) => (
  <div className={styles.progressBanner} role="status" aria-label="Regenerating exploit chains">
    <p className={styles.metaText}>
      <Spinner size="16" />
      {joinMetaSegments([
        `Regenerating — ${PHASE_LABELS[phase]} (${SUPPORTED_EXPLOIT_CHAINS_PHASES.indexOf(phase) + 1}/${SUPPORTED_EXPLOIT_CHAINS_PHASES.length})`,
        requestedBy && `requested by ${requestedBy}`,
        startedAt && `started ${formatRelativeTime(startedAt)}`,
      ])}
    </p>
  </div>
)

type ExploitChainsFailedBannerProps = {
  failureReason: string | null | undefined
  onRetry: () => unknown
  isRetrying: boolean
  canRetry: boolean
}

const ExploitChainsFailedBanner: React.FC<ExploitChainsFailedBannerProps> = ({
  failureReason,
  onRetry,
  isRetrying,
  canRetry,
}) => (
  <div className={styles.failedBanner} role="alert">
    <div className={styles.failedBannerHeader}>
      <p className={styles.failedBannerTitle}>Exploit chains generation failed</p>
      <DefaultButton
        buttonType="secondary"
        size="small"
        state={isRetrying ? 'loading' : canRetry ? 'default' : 'disabled'}
        onClick={onRetry}
      >
        Try again
      </DefaultButton>
    </div>
    {failureReason && <p className={styles.failedBannerReason}>{failureReason}</p>}
  </div>
)

type ExploitChainsMetaRowProps = {
  generatedAt: string | null | undefined
  branch?: string
  commitSha?: string
  requestedBy?: string
  chainCount: number
  onRegenerate: () => unknown
  isStarting: boolean
  canRegenerate: boolean
}

const ExploitChainsMetaRow: React.FC<ExploitChainsMetaRowProps> = ({
  generatedAt,
  branch,
  commitSha,
  requestedBy,
  chainCount,
  onRegenerate,
  isStarting,
  canRegenerate,
}) => {
  const shortSha = commitSha?.slice(0, 7)
  const provenance = [branch, shortSha ? `@ ${shortSha}` : undefined].filter(Boolean).join(' ')

  return (
    <div className={styles.metaRow}>
      <p className={styles.metaText}>
        {joinMetaSegments([
          generatedAt && `Generated ${formatDate({ date: generatedAt, type: 'mm/dd/yyyy-hh:mm' })}`,
          provenance,
          `${chainCount} ${pluralize(chainCount, 'chain', 'chains')}`,
          requestedBy && `requested by ${requestedBy}`,
        ])}
      </p>
      <DefaultButton
        buttonType="secondary"
        size="small"
        state={isStarting ? 'loading' : canRegenerate ? 'default' : 'disabled'}
        onClick={onRegenerate}
      >
        Regenerate
      </DefaultButton>
    </div>
  )
}

// #endregion

// #region Chain card list

type ExploitChainCardListProps = {
  chains: ChainSummary[]
  onSelect: (chainId: string) => unknown
  isDimmed?: boolean
}

/**
 * A linked finding's id is only unique within its scan, so key on the canonical
 * scan-scoped URL when the platform resolved one, and fall back to the bare id.
 */
const findingKey = (finding: LinkedFinding): string => finding._links?.self.href ?? `unresolved:${finding.findingId}`

/**
 * What to show for a finding. The id alone is meaningless to a reader — it is a
 * scan-scoped ordinal — so prefer the title the scanner gave it, then the rule
 * (a vulnerability class when it is a name, e.g. `sql-injection`), then location.
 */
const findingLabel = (finding: LinkedFinding): string =>
  finding.title ?? finding.rule ?? finding.path ?? finding.findingId

/**
 * The tool that reported the finding, which tells a reader how much the step is
 * worth trusting.
 *
 * Uses `tool`, which the platform resolves from the finding itself, rather than
 * `kind`. `kind` is the correlation agent's guess and it guessed `sast` for
 * everything — including DAST findings — so it is not worth promoting to a
 * badge. `tool` is absent when the finding backreference could not be resolved,
 * and then nothing is shown.
 */
const findingKindLabel = (finding: LinkedFinding): string => finding.tool ?? ''

/**
 * Deep link into Pixee's own drawer for the finding, so a reader can act on it
 * rather than just read about it: the fix when one exists, otherwise the triage.
 *
 * Needs the `(scanId, findingId)` pair — a finding id on its own is a scan-scoped
 * ordinal and addresses nothing. The pair comes from the backreference link, which
 * the platform omits when it cannot resolve the finding; those render as plain
 * text rather than as a link that would resolve to nothing.
 */
const findingDrawerPath = (finding: LinkedFinding): string | undefined => {
  const scanId = scanIdFromFindingBackreference(finding._links)
  if (!scanId) {
    return undefined
  }
  return Routes.ScanDetailsPage.createPath(
    finding.fixAvailable === true
      ? { scanId, fixFindingId: finding.findingId }
      : { scanId, triageFindingId: finding.findingId }
  )
}

const fixableCount = (findings: readonly LinkedFinding[]): number =>
  findings.filter(finding => finding.fixAvailable === true).length

/**
 * Whether we were told anything at all about fixability.
 *
 * A chain generated before fix availability was reported carries `undefined` on
 * every finding, which is not the same as "no fixes exist" — saying so would be
 * a claim we cannot support.
 */
const hasFixAvailabilityData = (findings: readonly LinkedFinding[]): boolean =>
  findings.some(finding => finding.fixAvailable !== undefined)

/**
 * Findings with a fix ready come first: breaking a chain means fixing any one of
 * its links, so the cheapest way out should be what the reader sees first.
 */
const byRemediationPriority = (findings: readonly LinkedFinding[]): LinkedFinding[] =>
  [...findings].sort((a, b) => Number(b.fixAvailable === true) - Number(a.fixAvailable === true))

const ExploitChainCardList: React.FC<ExploitChainCardListProps> = ({ chains, onSelect, isDimmed = false }) => (
  <section
    aria-label="Exploit chains"
    className={classNames(styles.cardList, { [styles.cardListDimmed]: isDimmed })}
    data-testid="exploit-chains-card-list"
  >
    {chains.map(chain => (
      <ExploitChainCard key={chain.chainId} chain={chain} onSelect={onSelect} />
    ))}
  </section>
)

type ExploitChainCardProps = {
  chain: ChainSummary
  onSelect: (chainId: string) => unknown
}

const ExploitChainCard: React.FC<ExploitChainCardProps> = ({ chain, onSelect }) => {
  const severity = chain.severity ?? ''
  const linkedFindings = chain.linkedFindings ?? []
  const stepCount = chain.stepCount ?? 0
  const level = toSeverityLevel(severity)

  return (
    <button
      className={styles.card}
      onClick={() => onSelect(chain.chainId)}
      aria-label={`View exploit chain: ${chain.title}`}
    >
      <div className={styles.cardHeader}>
        <p className={styles.cardTitle}>{chain.title}</p>
        <span className={styles.severityTag({ level })} data-severity={level}>
          {severity || 'Unknown'}
        </span>
      </div>
      <div className={styles.chipRow}>
        {linkedFindings.map(finding => (
          <span key={findingKey(finding)} className={styles.chip} title={findingLabel(finding)}>
            <span className={styles.chipRule}>{findingKindLabel(finding)}</span>
            <span className={styles.chipLabel}>{findingLabel(finding)}</span>
          </span>
        ))}
      </div>
      <div className={styles.cardFooter}>
        <p className={styles.cardMeta}>
          {joinMetaSegments([
            `${stepCount} ${pluralize(stepCount, 'step', 'steps')}`,
            `${linkedFindings.length} linked ${pluralize(linkedFindings.length, 'finding', 'findings')}`,
            fixableCount(linkedFindings) > 0 ? `${fixableCount(linkedFindings)} fixable` : undefined,
          ])}
        </p>
        <span className={styles.viewAffordance}>
          View
          <ArrowRightIcon size={16} weight="light" />
        </span>
      </div>
    </button>
  )
}

// #endregion

// #region Detail drawer

type LinkedFindingListProps = {
  findings: readonly LinkedFinding[]
}

/**
 * The findings a chain is built from, ordered so the ones with a fix ready come
 * first. A chain breaks when any single link in it is fixed, so this list is the
 * reader's shortest path out — not just provenance for the narrative below it.
 */
const LinkedFindingList: React.FC<LinkedFindingListProps> = ({ findings }) => {
  if (findings.length === 0) {
    return null
  }
  const fixable = fixableCount(findings)

  return (
    <section className={styles.linkedFindings} aria-label="Linked findings">
      <p className={styles.linkedFindingsTitle}>Linked findings</p>
      <p className={styles.linkedFindingsHint}>
        {match({ fixable, known: hasFixAvailabilityData(findings) })
          .with(
            { known: false },
            () =>
              'Fixing any one of these breaks the chain. Fix availability was not recorded when this chain was generated — regenerate it to see which findings are fixable.'
          )
          .when(
            ({ fixable: count }) => count > 0,
            ({ fixable: count }) =>
              `Fixing any one of these breaks the chain. ${count} of ${findings.length} ${pluralize(count, 'has', 'have')} a fix available, shown first.`
          )
          .otherwise(() => 'Fixing any one of these breaks the chain. None currently have an available fix.')}
      </p>
      {byRemediationPriority(findings).map(finding => {
        const kind = findingKindLabel(finding)
        const drawerPath = findingDrawerPath(finding)
        return (
          <div key={findingKey(finding)} className={styles.linkedFindingRow}>
            {kind ? <span className={styles.chipRule}>{kind}</span> : null}
            {drawerPath ? (
              <Link className={styles.linkedFindingLink} to={drawerPath}>
                {findingLabel(finding)}
              </Link>
            ) : (
              <span className={styles.linkedFindingLabel}>{findingLabel(finding)}</span>
            )}
            {finding.path && finding.path !== findingLabel(finding) ? (
              <span className={styles.linkedFindingPath}>{finding.path}</span>
            ) : null}
            {finding.fixAvailable === true ? <span className={styles.fixAvailableTag}>Fix available</span> : null}
          </div>
        )
      })}
    </section>
  )
}

type ExploitChainDetailDrawerProps = {
  chain: ChainSummary
  branch?: string
  onClose: () => unknown
}

const ExploitChainDetailDrawer: React.FC<ExploitChainDetailDrawerProps> = ({ chain, branch, onClose }) => {
  const severity = chain.severity ?? ''
  const linkedFindings = chain.linkedFindings ?? []
  const stepCount = chain.stepCount ?? 0
  const level = toSeverityLevel(severity)

  return (
    <Dialog.Root open={true} modal={false}>
      <Dialog.Portal>
        <Dialog.Content
          className={styles.drawerContent}
          onEscapeKeyDown={onClose}
          onPointerDownOutside={event => event.preventDefault()}
          onInteractOutside={event => event.preventDefault()}
        >
          <Dialog.Close asChild className={styles.drawerCloseButton}>
            <button aria-label="Close" onClick={onClose}>
              <XIcon size={24} weight="light" />
            </button>
          </Dialog.Close>
          <div className={styles.drawerHeader}>
            <Dialog.Title className={styles.drawerTitle}>{chain.title}</Dialog.Title>
            <Dialog.Description asChild>
              <p className={styles.drawerMeta}>
                <span className={styles.severityTag({ level })} data-severity={level}>
                  {severity || 'Unknown'}
                </span>
                {joinMetaSegments([
                  branch,
                  `${stepCount} ${pluralize(stepCount, 'step', 'steps')}`,
                  `${linkedFindings.length} linked ${pluralize(linkedFindings.length, 'finding', 'findings')}`,
                ])}
              </p>
            </Dialog.Description>
          </div>
          <LinkedFindingList findings={linkedFindings} />
          <RenderedMarkdown markdown={chain.detailMarkdown ?? ''} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// #endregion
